import { BaseService } from '../../core/BaseService';
import {
  Filters,
  GetAvgTicketPriceResponse,
  GetAvgTimePerJobCardResponse,
  GetBayInformationResponse,
  GetCompletedWorkOrdersResponse,
  GetConsumptionMonthlyTrendResponse,
  GetCustomerBreakdownResponse,
  GetCustomerRatingResponse,
  GetCustomerRetentionResponse,
  GetCustomerVisitPerformanceResponse,
  GetFleetAvailabilityResponse,
  GetHealthKPIsResponse,
  GetJobCardOpenVsClosedResponse,
  GetMonthlyPurchaseOrdersResponse,
  GetNetSalesStationResponse,
  GetNetSalesTechnicianResponse,
  GetOperationalKPIsResponse,
  GetPaymentsOverviewResponse,
  GetRevenueCostProfitResponse,
  GetServiceByVehicleTypeResponse,
  GetServiceOnTimeResponse,
  GetStationExpenseResponse,
  GetStationExpenseTypeResponse,
  GetTargetVsSalesResponse,
  GetTechnicianProductivityResponse,
  GetTopConsumedPartsResponse,
  GetTopSalesResponse,
  GetTopServicesPerformedResponse,
  GetVehicleTopServicesResponse,
  GetVehicleTurnaroundTimeResponse,
  GetWorkInProgressResponse,
} from './interface';

export class AnalyticsProfile extends BaseService {
  async getNetSales(req: Filters) {
    return this.get<GetNetSalesStationResponse>(`${this.apiUrl}/api/dashboards/netsales`, req);
  }

  async getTargetVsSales(req: Filters) {
    return this.get<GetTargetVsSalesResponse>(`${this.apiUrl}/api/dashboards/targetvssales`, req);
  }

  async getWorkInProgress(req: Filters) {
    return this.get<GetWorkInProgressResponse>(`${this.apiUrl}/api/dashboards/workinprogress`, req);
  }

  async getCompletedWorkOrders(req: Filters) {
    return this.get<GetCompletedWorkOrdersResponse>(`${this.apiUrl}/api/dashboards/completedjobs`, req);
  }

  async getRevenueCostProfit(req: Filters) {
    return this.get<GetRevenueCostProfitResponse>(`${this.apiUrl}/api/dashboards/revenuecostandprofit`, req);
  }

  async getTopSales(req: Filters, typeName: string) {
    return this.get<GetTopSalesResponse>(`${this.apiUrl}/api/dashboards/${typeName}`, req);
  }

  async getNetSalesTechnicians(req: Filters) {
    return this.get<GetNetSalesTechnicianResponse>(`${this.apiUrl}/api/dashboards/netsalestechnician`, req);
  }

  async getAvgTicketPrice(req: Filters) {
    return this.get<GetAvgTicketPriceResponse>(`${this.apiUrl}/api/dashboards/avgticketprice`, req);
  }

  async getPaymentsOverview(req: Filters) {
    return this.get<GetPaymentsOverviewResponse>(`${this.apiUrl}/api/dashboards/paymentdetails`, req);
  }

  async getStationExpenseWeekly(req: Filters) {
    return this.get<GetStationExpenseResponse>(`${this.apiUrl}/api/dashboards/station/expenses/weekly`, req);
  }

  async getStationExpenseType(req: Filters) {
    return this.get<GetStationExpenseTypeResponse>(`${this.apiUrl}/api/dashboards/station/expenses/bytype`, req);
  }

  async getServiceCancellationReasons(req: Filters) {
    return this.get<GetStationExpenseTypeResponse>(`${this.apiUrl}/api/dashboards/canceledservices`, req);
  }

  async getServiceOnTime(req: Filters) {
    return this.get<GetServiceOnTimeResponse>(`${this.apiUrl}/api/dashboards/serviceontime`, req);
  }

  async getSosData(req: Filters) {
    return this.get<GetServiceOnTimeResponse>(`${this.apiUrl}/api/dashboards/sos`, req);
  }

  async getTechnicianProductivity(req: Filters) {
    return this.get<GetTechnicianProductivityResponse>(`${this.apiUrl}/api/dashboards/technician-productivity`, req);
  }

  async getFleetAvailability(req: Filters) {
    return this.get<GetFleetAvailabilityResponse>(`${this.apiUrl}/api/dashboards/fleetavailability`, req);
  }

  async getVehicleTurnaround(req: Filters) {
    return this.get<GetVehicleTurnaroundTimeResponse>(`${this.apiUrl}/api/dashboards/vehicleturnaround`, req);
  }

  async getBayInformation(req: Filters) {
    return this.get<GetBayInformationResponse>(`${this.apiUrl}/api/dashboards/baycount`, req);
  }

  async getJobCardOpenVsClosed(req: Filters) {
    return this.get<GetJobCardOpenVsClosedResponse>(`${this.apiUrl}/api/dashboards/jcopenvsclosed`, req);
  }

  async getAvgTimePerJobCard(req: Filters) {
    return this.get<GetAvgTimePerJobCardResponse>(`${this.apiUrl}/api/dashboards/avgtimeperjc`, req);
  }

  async getCustomerBreakdown(req: Filters) {
    return this.get<GetCustomerBreakdownResponse>(`${this.apiUrl}/api/dashboards/customerstats`, req);
  }

  async getCustomerSummary(req: Filters) {
    return this.get<GetCustomerBreakdownResponse>(`${this.apiUrl}/api/dashboards/customer-summary`, req);
  }

  async getTopServicesPerformed(req: Filters) {
    return this.get<GetTopServicesPerformedResponse>(`${this.apiUrl}/api/dashboards/topservicesperformed`, req);
  }

  async getVehicleTopServices(req: Filters) {
    return this.get<GetVehicleTopServicesResponse>(`${this.apiUrl}/api/dashboards/servicebreakdownbyvehicle`, req);
  }

  async getServiceByVehicleType(req: Filters) {
    return this.get<GetServiceByVehicleTypeResponse>(`${this.apiUrl}/api/dashboards/servicebyvehicletype`, req);
  }

  async getCustomerRetention(req: Filters) {
    return this.get<GetCustomerRetentionResponse>(`${this.apiUrl}/api/dashboards/customer-retention`, req);
  }

  async getCustomerRating(req: Filters) {
    return this.get<GetCustomerRatingResponse>(`${this.apiUrl}/api/dashboards/customer-rating`, req);
  }

  async getCustomerVisitPerformance(req: Filters) {
    return this.get<GetCustomerVisitPerformanceResponse>(`${this.apiUrl}/api/dashboards/customer-visit-performance`, req);
  }

  async getTopConsumedParts(req: Filters) {
    return this.get<GetTopConsumedPartsResponse>(
      `${this.apiUrl}/api/dashboards/inventory/spare-parts/top-consumed`,
      req
    );
  }

  async getMonthlyPurchaseOrders(req: Filters) {
    return this.get<GetMonthlyPurchaseOrdersResponse>(
      `${this.apiUrl}/api/dashboards/inventory/purchase-orders/monthly`,
      req
    );
  }

  async getOperationalKPIs(req: Filters) {
    return this.get<GetOperationalKPIsResponse>(`${this.apiUrl}/api/dashboards/inventory/kpis/operational`, req);
  }

  async getHealthKPIs(req: Filters) {
    return this.get<GetHealthKPIsResponse>(`${this.apiUrl}/api/dashboards/inventory/kpis/health`, req);
  }

  async getConsumptionMonthlyTrend(req: Filters) {
    return this.get<GetConsumptionMonthlyTrendResponse>(
      `${this.apiUrl}/api/dashboards/inventory/consumption/trend`,
      req
    );
  }
}
